import { TournamentState } from '@ps/poker-games-tile-mappers';
import type { PowerPathOverviewTab } from '@ps/web-martech-cms-types';

import type { FilterVariables, Tournament } from '@swf/poker';
import { criteriaMatches, filterMTTTournaments, parseHSPCriteria } from '@swf/poker';

import type { PPDataType, Pass, PassBundleRaw, StepKey } from '../types';
import { PowerPathType, StepKeyName } from '../types';

export const getPPDataTypeKey = (dataType: string): PPDataType =>
  dataType === 'mock' ? 'mock' : 'real';

export const generateStepKey = (step: number, type: PowerPathType): StepKey => {
  if (step === 1) {
    return StepKeyName.step1;
  } else if (step === 2) {
    if (type === PowerPathType.SIT_AND_GO) {
      return StepKeyName.step2SNG;
    }
    return StepKeyName.step2Scheduled;
  } else if (step === 3) {
    return StepKeyName.step3;
  } else if (step === 4) {
    return StepKeyName.step4;
  }
  throw new Error('Invalid step or type provided');
};

export const normalizePassesResponse = (
  passesResponse: PassBundleRaw,
  site: string,
  country: string
): PassBundleRaw => {
  if (!passesResponse) return { gold: [], silver: [], bronze: [] };

  const { gold, silver, bronze } = passesResponse;
  return {
    gold: filterPassesData(gold, site, country),
    silver: filterPassesData(silver, site, country),
    bronze: filterPassesData(bronze, site, country)
  };
};

export const filterTournaments = (
  filterVariables: FilterVariables,
  tournaments: Tournament[]
): Tournament[] =>
  filterMTTTournaments(filterVariables, tournaments).filter((tournament) => {
    if (tournament.isSnG) {
      return ![TournamentState.RUNNING, TournamentState.COMPLETED].includes(
        tournament.formattedState
      );
    }

    return true;
  });

const filterPassesData = (passes: Pass[] = [], site: string, country: string): Pass[] =>
  passes
    .filter((pass) => {
      const parsedCriteria = parseHSPCriteria(pass.targeting);
      return criteriaMatches(parsedCriteria, {
        site: [site?.toLowerCase() || ''],
        country: [country?.toLowerCase() || '']
      });
    })
    .sort((a, b) => a.order - b.order);

interface OverviewSections {
  tilesWithImageAndDescription: {
    visibility: {
      root: boolean;
    };
    data: PowerPathOverviewTab['tiles_with_image_and_description'];
  };
  playForFreeDescription: {
    visibility: {
      root: boolean;
      description: boolean;
    };
    data: PowerPathOverviewTab['play_for_free_description'];
  };
  howItWorksVideo: {
    visibility: {
      root: boolean;
      video: boolean;
    };
    data: PowerPathOverviewTab['how_it_works_video'];
  };
}

export const getOverviewSections = (
  overviewTab?: PowerPathOverviewTab
): OverviewSections | null => {
  if (!overviewTab) return null;

  const {
    tiles_with_image_and_description: tilesWithImageAndDescription,
    play_for_free_description: playForFreeDescription,
    how_it_works_video: howItWorksVideo
  } = overviewTab;

  const showTilesWithImageAndDescription = !!(
    tilesWithImageAndDescription && tilesWithImageAndDescription.simple_tiles.length
  );
  const showPlayForFreeDescription = !!(
    playForFreeDescription?.heading.text ||
    playForFreeDescription?.heading.accented_text ||
    playForFreeDescription?.description ||
    playForFreeDescription?.cta.title
  );
  const showHowItWorksVideoBrightcove = !!(
    howItWorksVideo?.brightcove_video.brightcove_video_id &&
    howItWorksVideo?.brightcove_video.brightcove_player_id
  );
  const showHowItWorksVideo = !!(
    howItWorksVideo?.heading.text ||
    howItWorksVideo?.heading.accented_text ||
    showHowItWorksVideoBrightcove
  );

  const overviewSections = {
    tilesWithImageAndDescription: {
      visibility: {
        root: showTilesWithImageAndDescription
      },
      data: tilesWithImageAndDescription
    },
    playForFreeDescription: {
      visibility: {
        root: showPlayForFreeDescription,
        description: !!playForFreeDescription?.description
      },
      data: playForFreeDescription
    },
    howItWorksVideo: {
      visibility: {
        root: showHowItWorksVideo,
        video: showHowItWorksVideoBrightcove
      },
      data: howItWorksVideo
    }
  };

  return overviewSections;
};
